<template>
  <div ref="headerRef" class="fixed top-0 z-30 w-full">
    <AppAdvertisementBanners />
    <div
      class="bg-white dark:bg-dark-surface px-4 py-2.5 lg:px-12 lg:py-2.5 flex flex-row flex-wrap justify-between border-b-0.5 border-grey-11 dark:border-dark-border"
    >
      <div class="h-full flex-3">
        <router-link aria-label="Home" to="/">
          <AppLogo class="w-40 h-auto lg:w-[220px]" />
        </router-link>
      </div>

      <div v-if="userStore.isAuthenticated" class="flex-1 flex justify-end items-center">
        <AppThemeToggle />

        <!-- App Switcher -->
        <div class="mr-2">
          <AppSwitcher />
        </div>

        <a
          v-for="link in headerLinks"
          :key="link.link"
          :href="link.link"
          :target="link.newTab ? '_blank' : undefined"
          :rel="link.newTab ? 'noopener noreferrer' : undefined"
          class="no-underline my-auto"
          :class="link.desktopOnly ? 'hidden lg:flex' : 'flex'"
        >
          <mdicon :size="20" class="mr-2 text-grey-11 dark:text-dark-ink-muted" :aria-label="link.title" :name="link.icon" /></a
        >
        
        <!-- Mobile Menu Button -->
        <div
          class="cursor-pointer lg:hidden flex no-underline my-auto ml-4"
          @click.prevent="sidebarStore.toggleExpanded"
        >
          <mdicon :size="20" aria-label="Menu" class="transition-all text-grey-11 dark:text-dark-ink-muted" :name="menuIcon" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import AppLogo from '../logo/AppLogo.vue'
import AppAdvertisementBanners from '../advertisement/AppAdvertisementBanners.vue'
import AppSwitcher from '../app-switcher/AppSwitcher.vue'
import AppThemeToggle from '../theme/AppThemeToggle.vue'
import { useUserStore } from '../../../stores/userStore.js'
import { useSidebarStore } from '../../../stores/sidebarStore.js'
import { USER_PERMISSIONS } from '../../../constants/userPermissions.ts'

const userStore = useUserStore()
const sidebarStore = useSidebarStore()

const hasAdminAccess = computed(
  () => !!userStore.userPermissions[USER_PERMISSIONS.IS_HUB_ADMIN]
)

const headerRef = ref(null)
const resizeObserver = ref(null)

const menuIcon = computed(() => {
  return sidebarStore.sidebarIsExpanded ? 'close' : 'menu'
})

const headerLinks = computed(() => {
  const links = []

  if (hasAdminAccess.value) {
    links.push({
      title: 'Admin Panel',
      link: '/admin',
      icon: 'shield-account',
      newTab: true,
      desktopOnly: true,
    })
  }

  links.push({
    title: userStore.isAuthenticated ? 'Logout' : 'Login',
    link: userStore.isAuthenticated ? '/auth/logout' : '/auth/login',
    icon: userStore.isAuthenticated ? 'logout' : 'login',
    newTab: false,
  })

  return links
})

const emitHeightChange = () => {
  if (headerRef.value) {
    const height = headerRef.value.offsetHeight
    emit('height-change', height)
  }
}

onMounted(() => {
  emitHeightChange()
  window.addEventListener('resize', emitHeightChange)

  resizeObserver.value = new ResizeObserver(() => {
    emitHeightChange()
  })
  if (headerRef.value) {
    resizeObserver.value.observe(headerRef.value)
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', emitHeightChange)
  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
  }
})

const emit = defineEmits(['height-change'])
</script>
